import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Upload, Image as ImageIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROOM_TYPES = [
  { value: 'cuisine', label: 'Cuisine' },
  { value: 'salle_de_bain', label: 'Salle de bain' },
  { value: 'complete', label: 'Rénovation complète' },
  { value: 'partielle', label: 'Rénovation partielle' },
  { value: 'peinture', label: 'Peinture' },
  { value: 'parquet', label: 'Parquet' },
  { value: 'carrelage', label: 'Carrelage' }
];

export default function UploadTestSection() {
  const [loading, setLoading] = useState(false);
  const [beforeImage, setBeforeImage] = useState(null);
  const [afterImage, setAfterImage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    room_type: '',
    location: '',
    duration_weeks: '',
    what_was_managed: '',
    description: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (file, type) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${BACKEND_URL}/api/uploads`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (type === 'before') {
        setBeforeImage(response.data.url);
      } else {
        setAfterImage(response.data.url);
      }
      
      toast.success(`Image ${type === 'before' ? 'avant' : 'après'} uploadée`);
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.room_type || !beforeImage || !afterImage || !formData.what_was_managed) {
      toast.error('Veuillez remplir tous les champs obligatoires et uploader les 2 images');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        duration_weeks: formData.duration_weeks ? parseInt(formData.duration_weeks) : null,
        before_image_url: beforeImage,
        after_image_url: afterImage,
        featured: false
      };

      await axios.post(`${BACKEND_URL}/api/gallery-cases`, payload);
      
      toast.success('Cas ajouté à la galerie !', {
        description: 'Consultez l\'onglet Galerie pour le voir'
      });

      // Reset
      setFormData({
        title: '',
        room_type: '',
        location: '',
        duration_weeks: '',
        what_was_managed: '',
        description: ''
      });
      setBeforeImage(null);
      setAfterImage(null);
    } catch (error) {
      toast.error('Erreur lors de la création du cas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <Card className="rounded-2xl border-[color:var(--hr-border)]">
        <CardHeader>
          <CardTitle className="font-['Playfair_Display'] text-2xl" style={{ color: 'var(--hr-text)' }}>
            Créer un cas avant/après
          </CardTitle>
          <CardDescription className="text-sm" style={{ color: 'var(--hr-text-muted)' }}>
            Uploadez 2 images et créez un cas de test pour la galerie
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Images upload */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium mb-2 block" style={{ color: 'var(--hr-text)' }}>
                Image "Avant" *
              </Label>
              <div 
                className="relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:bg-[color:var(--hr-bg-alt)] transition-colors"
                style={{ borderColor: 'var(--hr-border)' }}
              >
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={(e) => e.target.files[0] && handleImageUpload(e.target.files[0], 'before')}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  data-testid="upload-before-image"
                />
                {beforeImage ? (
                  <div>
                    <ImageIcon className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--hr-success)' }} />
                    <p className="text-xs" style={{ color: 'var(--hr-text-muted)' }}>Image uploadée ✓</p>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--hr-text-muted)' }} />
                    <p className="text-xs" style={{ color: 'var(--hr-text-muted)' }}>Cliquez pour uploader</p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium mb-2 block" style={{ color: 'var(--hr-text)' }}>
                Image "Après" *
              </Label>
              <div 
                className="relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:bg-[color:var(--hr-bg-alt)] transition-colors"
                style={{ borderColor: 'var(--hr-border)' }}
              >
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={(e) => e.target.files[0] && handleImageUpload(e.target.files[0], 'after')}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  data-testid="upload-after-image"
                />
                {afterImage ? (
                  <div>
                    <ImageIcon className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--hr-success)' }} />
                    <p className="text-xs" style={{ color: 'var(--hr-text-muted)' }}>Image uploadée ✓</p>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--hr-text-muted)' }} />
                    <p className="text-xs" style={{ color: 'var(--hr-text-muted)' }}>Cliquez pour uploader</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Form fields */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="title" className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>Titre *</Label>
              <Input 
                id="title"
                placeholder="Ex: Rénovation complète appartement 3 pièces"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                className="rounded-xl mt-1.5"
                data-testid="upload-case-title"
              />
            </div>

            <div>
              <Label htmlFor="room_type" className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>Type *</Label>
              <Select value={formData.room_type} onValueChange={(v) => handleChange('room_type', v)}>
                <SelectTrigger id="room_type" className="rounded-xl mt-1.5" data-testid="upload-case-type">
                  <SelectValue placeholder="Sélectionnez le type" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {ROOM_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="location" className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>Ville</Label>
                <Input 
                  id="location"
                  placeholder="Ex: Genève"
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  className="rounded-xl mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="duration" className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>Durée (semaines)</Label>
                <Input 
                  id="duration"
                  type="number"
                  placeholder="Ex: 8"
                  value={formData.duration_weeks}
                  onChange={(e) => handleChange('duration_weeks', e.target.value)}
                  className="rounded-xl mt-1.5"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="managed" className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>Ce qui a été piloté *</Label>
              <Textarea 
                id="managed"
                placeholder="Ex: Coordination de 4 corps de métier, suivi hebdomadaire, contrôles qualité..."
                value={formData.what_was_managed}
                onChange={(e) => handleChange('what_was_managed', e.target.value)}
                className="rounded-xl mt-1.5"
                data-testid="upload-case-managed"
              />
            </div>
          </div>

          <Button 
            onClick={handleSubmit}
            disabled={loading}
            className="w-full rounded-xl"
            style={{ backgroundColor: 'var(--hr-primary)', color: 'var(--hr-primary-fg)' }}
            data-testid="upload-case-submit"
          >
            {loading ? 'Création...' : 'Créer le cas'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}